import routes from './routes.json';

/**
 * Single source of truth for everything search engines need to know about the
 * business: canonical domain, NAP (name/address/phone), geo, and route metadata.
 *
 * Keep this in sync with the Google Business Profile. Search engines cross-check
 * the NAP here against the profile and other directory listings; any mismatch
 * (a different suite number, a different phone format) weakens local ranking.
 */

// Never point this at goodluckmetals.com — that domain belongs to an unrelated
// company, and a canonical pointing there deindexes this site.
export const SITE_URL = 'https://goodluckmetals.ae';

export const ORG_NAME = 'Good Luck Metals';
export const ORG_LEGAL_NAME = 'Good Luck Metals Trading LLC';

export const CONTACT = {
  email: 'info@goodluckmetals.ae',
  phone: '+971504728292',
  phoneAlt: '+971506333849',
};

/**
 * VERIFY against the Google Business Profile before the next deploy. Address
 * lines and coordinates that disagree with the profile suppress the map pack.
 * `latitude`/`longitude` are the Port Saeed / Deira block, not a surveyed pin.
 */
export const ADDRESS = {
  streetAddress: 'Office 402, City Avenue Building, Port Saeed',
  addressLocality: 'Deira, Dubai',
  addressRegion: 'Dubai',
  addressCountry: 'AE',
  latitude: 25.2528,
  longitude: 55.3306,
};

/** VERIFY against the Google Business Profile — wrong hours show in search results. */
export const OPENING_HOURS = [
  { days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], opens: '08:00', closes: '18:00' },
];

/**
 * Real, claimed profiles only. `sameAs` is an entity-reconciliation signal:
 * pointing it at a bare platform homepage the business does not own is noise at
 * best and a trust hit at worst. Add each URL as the profile goes live.
 */
export const SOCIAL_PROFILES: string[] = [];

/** Emirates and markets served — feeds `areaServed` and the local landing copy. */
export const AREAS_SERVED = [
  'Dubai',
  'Abu Dhabi',
  'Sharjah',
  'Ajman',
  'Ras Al Khaimah',
  'Fujairah',
  'Umm Al Quwain',
  'United Arab Emirates',
];

/** Detailed 7 Emirates data for GEO and AEO entity linking */
export const UAE_EMIRATES = [
  {
    name: 'Dubai',
    iso: 'AE-DU',
    hubs: ['Port Saeed / Deira (HQ)', 'Al Quoz Industrial Areas 1–4', 'Ras Al Khor Industrial', 'Jebel Ali Free Zone (JAFZA)', 'Dubai Industrial City (DIC)'],
    focusMetals: ['Copper Millberry & Berry', 'Aluminium 6063 Extrusions', 'Yellow Brass Honey', 'SS 304/316', 'Soft Lead'],
    pickupTurnaround: 'Same-day collection and certified digital weighbridge',
  },
  {
    name: 'Abu Dhabi',
    iso: 'AE-AZ',
    hubs: ['Mussafah Industrial Area (Sectors 1–44)', 'ICAD I, II & III', 'KEZAD / KIZAD', 'Al Ain Industrial City'],
    focusMetals: ['High Nickel Alloys (Inconel, Monel, Hastelloy)', 'Heavy Copper Busbars', 'Cupronickel 90/10 & 70/30', 'Aluminium Structural Scrap'],
    pickupTurnaround: '24-hour dispatch for industrial flatbeds and roll-off containers',
  },
  {
    name: 'Sharjah',
    iso: 'AE-SH',
    hubs: ['Sharjah Industrial Areas 1–18', 'Sajaa Industrial Oasis', 'Hamriyah Free Zone'],
    focusMetals: ['Aluminium Sheet & Profiles', 'Brass Turnings & Pallu', 'Copper Armature Wire', 'Stainless Steel 304/316'],
    pickupTurnaround: 'Same-day on-demand skip bin placement and pickup',
  },
  {
    name: 'Ajman',
    iso: 'AE-AJ',
    hubs: ['Ajman Industrial Area 1 & 2', 'Al Jurf Industrial Zone'],
    focusMetals: ['Aluminium Extrusions (6063)', 'Clean Copper Pipes', 'Bronze Bearings', 'Mixed Non-Ferrous Solids'],
    pickupTurnaround: 'Daily collection routes for fabricators and workshops',
  },
  {
    name: 'Ras Al Khaimah',
    iso: 'AE-RK',
    hubs: ['Al Ghail Industrial Park', 'Al Hamra Industrial Zone', 'Al Hulaila'],
    focusMetals: ['Industrial Copper Cable', 'Aluminium Foundry Scrap', 'Zinc Die-Cast Scrap', 'Lead Sheets & Piping'],
    pickupTurnaround: 'Scheduled heavy-haulage logistics from plants and yards',
  },
  {
    name: 'Fujairah',
    iso: 'AE-FU',
    hubs: ['Fujairah Port Free Zone', 'Al Hayl Industrial Area', 'Dibba'],
    focusMetals: ['Marine Grade Bronze', 'Cupro-Nickel condenser tubes', 'Brass Ship Valves', 'Lead Ballast'],
    pickupTurnaround: 'Port and shipyard collection with certified maritime compliance',
  },
  {
    name: 'Umm Al Quwain',
    iso: 'AE-UQ',
    hubs: ['UAQ Industrial City', 'Umm Al Thaoub Industrial Area'],
    focusMetals: ['Aluminium Turnings & Offcuts', 'Copper Coils', 'SS Sheet Remnants', 'Mixed Non-Ferrous'],
    pickupTurnaround: 'Direct yard clearance and mobile scale weighing',
  },
];

export interface RouteMeta {
  path: string;
  title: string;
  description: string;
  keywords: string;
  /** Sitemap hints. */
  priority: number;
  changefreq: 'daily' | 'weekly' | 'monthly';
}

/**
 * Every indexable URL on the site.
 *
 * The data lives in routes.json rather than here so that scripts/generate-sitemap.js
 * can read the same file at build time. A route can therefore never end up in
 * the app but missing from the sitemap, or the reverse.
 */
export const ROUTES: Record<string, RouteMeta> = routes as Record<string, RouteMeta>;

export const absoluteUrl = (path: string): string =>
  path === '/' ? SITE_URL : `${SITE_URL}${path}`;
